import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBars,
  faChevronDown,
  faStethoscope,
  faHeart,
  faShoppingBag,
  faTimes,
  faUser,
  faLayerGroup,
  faInfoCircle,
  faRightFromBracket,
} from '@fortawesome/free-solid-svg-icons';
import './SecondaryNavbar.css';
import { fetchCategories } from '../utils/categories';

const SecondaryNavbar = ({
  user,
  onCategorySelect,
  onDoctorClick,
  onAboutClick,
  onFavoritesClick,
  onMyOrdersClick,
  onAccountClick,
  onLogout,
}) => {
  const [showCategoriesDropdown, setShowCategoriesDropdown] = useState(false);
  const [showServicesDropdown, setShowServicesDropdown] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [mobileCatsOpen, setMobileCatsOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const catsRef = useRef(null);
  const servicesRef = useRef(null);
  const catsHoverTimer = useRef(null);
  const servicesHoverTimer = useRef(null);

  useEffect(() => {
    let cancelled = false;
    fetchCategories()
      .then((rows) => {
        if (!cancelled) setCategories(rows);
      })
      .catch((err) => {
        console.error('Error loading categories:', err);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    document.body.style.overflow = sidebarOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [sidebarOpen]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (catsRef.current && !catsRef.current.contains(e.target)) {
        setShowCategoriesDropdown(false);
      }
      if (servicesRef.current && !servicesRef.current.contains(e.target)) {
        setShowServicesDropdown(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const closeSidebar = () => {
    setSidebarOpen(false);
    setMobileCatsOpen(false);
  };

  const run = (fn) => () => {
    if (fn) fn();
    closeSidebar();
  };

  const openCats = () => {
    clearTimeout(catsHoverTimer.current);
    setShowCategoriesDropdown(true);
    setShowServicesDropdown(false);
  };
  const scheduleCloseCats = () => {
    catsHoverTimer.current = setTimeout(() => setShowCategoriesDropdown(false), 180);
  };

  const openServices = () => {
    clearTimeout(servicesHoverTimer.current);
    setShowServicesDropdown(true);
    setShowCategoriesDropdown(false);
  };
  const scheduleCloseServices = () => {
    servicesHoverTimer.current = setTimeout(() => setShowServicesDropdown(false), 180);
  };

  return (
    <>
      <div className="secondary-navbar bottom-navbar">
        <button
          className="hamburger-button"
          onClick={() => setSidebarOpen(true)}
          aria-label="Open menu"
        >
          <FontAwesomeIcon icon={faBars} />
        </button>

        <div className="secondary-navbar-inner">
          <div
            className="dropdown-container desktop-only"
            ref={catsRef}
            onMouseEnter={openCats}
            onMouseLeave={scheduleCloseCats}
          >
            <button
              className="category-button"
              onClick={() => {
                setShowCategoriesDropdown((v) => !v);
                setShowServicesDropdown(false);
              }}
            >
              <FontAwesomeIcon icon={faBars} />
              All Categories
              <FontAwesomeIcon icon={faChevronDown} />
            </button>
            {showCategoriesDropdown && (
              <div className="dropdown-menu categories-menu">
                {categories.map((category) => (
                  <button
                    key={category.id}
                    className="dropdown-item"
                    onClick={() => {
                      onCategorySelect(category.id);
                      setShowCategoriesDropdown(false);
                    }}
                  >
                    {category.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="nav-links desktop-only">
            {user && (
              <>
                <button className="nav-link simple-link" onClick={onMyOrdersClick}>
                  <FontAwesomeIcon icon={faShoppingBag} /> My Orders
                </button>

                <button className="nav-link simple-link" onClick={onFavoritesClick}>
                  <FontAwesomeIcon icon={faHeart} /> Favourites
                </button>
              </>
            )}

            <div
              className="dropdown-container"
              ref={servicesRef}
              onMouseEnter={openServices}
              onMouseLeave={scheduleCloseServices}
            >
              <button
                className="nav-link"
                onClick={() => {
                  setShowServicesDropdown((v) => !v);
                  setShowCategoriesDropdown(false);
                }}
              >
                <FontAwesomeIcon icon={faStethoscope} />
                E-Services
                <FontAwesomeIcon icon={faChevronDown} />
              </button>
              {showServicesDropdown && (
                <div className="dropdown-menu services-menu">
                  <button
                    className="dropdown-item"
                    onClick={() => {
                      onDoctorClick();
                      setShowServicesDropdown(false);
                    }}
                  >
                    Consult a Doctor
                  </button>
                </div>
              )}
            </div>

            <button className="nav-link simple-link" onClick={onAboutClick}>
              About
            </button>
          </div>
        </div>

      </div>

      <div
        className={`sidebar-overlay ${sidebarOpen ? 'open' : ''}`}
        onClick={closeSidebar}
      />
      <aside className={`mobile-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <span className="sidebar-brand">AlamCareRx</span>
          <button
            className="sidebar-close"
            onClick={closeSidebar}
            aria-label="Close menu"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {user ? (
          <div className="sidebar-user">
            <div className="sidebar-user-info">
              <FontAwesomeIcon icon={faUser} />
              <div>
                <p className="sidebar-user-name">{user.name}</p>
                <p className="sidebar-user-email">{user.email}</p>
              </div>
            </div>
            <button className="sidebar-logout" onClick={run(onLogout)}>
              <FontAwesomeIcon icon={faRightFromBracket} /> Logout
            </button>
          </div>
        ) : (
          <button className="sidebar-login" onClick={run(onAccountClick)}>
            <FontAwesomeIcon icon={faUser} />
            Login &amp; Register
          </button>
        )}

        <nav className="sidebar-nav">
          <button
            className="sidebar-link has-sub"
            onClick={() => setMobileCatsOpen(!mobileCatsOpen)}
          >
            <span>
              <FontAwesomeIcon icon={faLayerGroup} /> All Categories
            </span>
            <FontAwesomeIcon
              icon={faChevronDown}
              className={`sub-arrow ${mobileCatsOpen ? 'rot' : ''}`}
            />
          </button>
          {mobileCatsOpen && (
            <div className="sidebar-sub">
              {categories.map((category) => (
                <button
                  key={category.id}
                  className="sidebar-sublink"
                  onClick={run(() => onCategorySelect(category.id))}
                >
                  {category.name}
                </button>
              ))}
            </div>
          )}

          {user && (
            <>
              <button className="sidebar-link" onClick={run(onMyOrdersClick)}>
                <span>
                  <FontAwesomeIcon icon={faShoppingBag} /> My Orders
                </span>
              </button>
              <button className="sidebar-link" onClick={run(onFavoritesClick)}>
                <span>
                  <FontAwesomeIcon icon={faHeart} /> Favourites
                </span>
              </button>
            </>
          )}
          <button className="sidebar-link" onClick={run(onDoctorClick)}>
            <span>
              <FontAwesomeIcon icon={faStethoscope} /> Consult a Doctor
            </span>
          </button>
          <button className="sidebar-link" onClick={run(onAboutClick)}>
            <span>
              <FontAwesomeIcon icon={faInfoCircle} /> About
            </span>
          </button>
        </nav>
      </aside>
    </>
  );
};

export default SecondaryNavbar;
